import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wrench } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/Spinner';
import { apiFetch } from '@/lib/api';

interface CrewPlan {
  steps?: { capability: string }[];
  rationale?: string;
}

/**
 * The Director's proposed plan for this production: which crew members run, in what
 * order, and why. Approving runs it; a note sends it back to be re-authored once.
 */
export default function CrewPlanGate({
  productionId,
  busy,
  onRequestChanges,
}: {
  productionId: string;
  busy: boolean;
  onRequestChanges: (note: string) => void;
}) {
  const { t } = useTranslation('production');
  const [plan, setPlan] = useState<CrewPlan | null>(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    apiFetch(`/api/assets/${productionId}/crew_plan.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setPlan)
      .catch(() => {
        /* display-only — the banner still lets the producer approve */
      });
  }, [productionId]);

  return (
    <div className="mt-3 space-y-2 border-t border-primary/25 pt-3">
      {plan?.steps && (
        <div className="flex flex-wrap items-center gap-1 text-xs">
          {plan.steps.map((s, i) => (
            <span key={`${s.capability}-${i}`} className="flex items-center gap-1">
              {i > 0 && <span className="text-faint">→</span>}
              <Badge variant="secondary" className="text-[10px]">
                {s.capability}
              </Badge>
            </span>
          ))}
        </div>
      )}
      {plan?.rationale && <p className="m-0 text-xs italic text-muted-foreground">“{plan.rationale}”</p>}
      <div className="flex items-end gap-2">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('gate.crewPlan.notePlaceholder')}
          rows={2}
          className="min-w-0 flex-1 rounded-md border border-border bg-background p-2 text-xs outline-none focus:border-primary"
        />
        <Button
          onClick={() => onRequestChanges(note.trim())}
          disabled={busy || !note.trim()}
          variant="secondary"
          className="shrink-0"
        >
          {busy ? <Spinner className="mr-1" /> : <Wrench className="mr-1 h-4 w-4" />}
          {t('gate.crewPlan.requestChanges')}
        </Button>
      </div>
    </div>
  );
}
